import type { Locale, PublicClip } from "@kmstories/shared";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { ShareBar } from "../components/ShareBar.js";
import { TrailerFacade } from "../components/TrailerFacade.js";
import { useT } from "../i18n/LocaleProvider.js";
import { api } from "../lib/api.js";
import { useDocumentTitle } from "../lib/useDocumentTitle.js";
import { NotFoundPage } from "./NotFound.js";

export function ClipPage() {
  const { m, lp, locale } = useT();
  const { slug } = useParams();
  const [data, setData] = useState<{ clip: PublicClip; captions: Record<Locale, string> } | null | "missing">(null);

  useEffect(() => {
    if (!slug) return;
    api
      .get<{ clip: PublicClip; captions: Record<Locale, string> }>(`/api/clips/${encodeURIComponent(slug)}?locale=${locale}`)
      .then(setData)
      .catch(() => setData("missing"));
    // Inbound share params (?s=…&c=…) have done their job; keep the URL clean.
    const u = new URL(window.location.href);
    if (u.searchParams.has("s") || u.searchParams.has("c")) {
      u.searchParams.delete("s");
      u.searchParams.delete("c");
      window.history.replaceState(null, "", u.pathname + u.search + u.hash);
    }
  }, [slug, locale]);

  useDocumentTitle(data && data !== "missing" ? `${data.clip.title} — ${m.meta.siteName}` : m.meta.pages.clips);

  if (data === "missing") return <NotFoundPage />;
  if (!data) {
    return (
      <div className="container container--narrow section">
        <p className="muted">{m.common.loading}…</p>
      </div>
    );
  }
  const { clip, captions } = data;
  return (
    <div className="container container--narrow">
      <div className="page-head">
        <p className="section__kicker">{m.clips.heading}</p>
        <h1>{clip.title}</h1>
        {clip.description && <p className="page-head__lede">{clip.description}</p>}
      </div>
      <TrailerFacade youtubeId={clip.youtubeId} format={clip.format} title={clip.title} thumbnailUrl={clip.thumbnailUrl} />
      <div className="card" style={{ marginBlockStart: "1.5rem" }}>
        <ShareBar clip={clip} captions={captions} />
      </div>
      <section className="section section--tight">
        <Link className="btn btn--ghost" to={lp("/clips")}>
          {m.clips.more}
        </Link>
        <div className="card card--paper" style={{ marginBlockStart: "1.25rem" }}>
          <h2 style={{ fontSize: "1.3rem" }}>{m.clips.backToFilm}</h2>
          <Link className="btn btn--primary" to={lp("/share")}>
            {m.clips.shareCta}
          </Link>
        </div>
      </section>
    </div>
  );
}
